<script>
import { http } from 'App/http';
import IntervalPicker from '../../apps/survey/IntervalPicker';
import Switchery from 'App/components/global/form/Switchery';

export default {
    props: {
        surveySettings: {
            required: false,
            default: null,
        },
        canSendSmsMessages: {
            required: true,
        },
        portal: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            booking: null,
            survey_id: null,
            sending_interval: null,
            send_email: false,
            send_sms: false,
            loading: false,
        }
    },

    mounted() {
        this.$event_bus.$on('addSurveyToBooking', booking => {
            this.$store.dispatch('surveys/load');

            this.booking = booking;
            this.survey_id = null;
            this.sending_interval = '+P2D';
            this.send_email = !!(this.surveySettings.send_survey_via_email && this.is_survey_sendable_by_email);
            this.send_sms = !!(this.surveySettings.send_survey_via_sms && this.is_survey_sendable_by_sms);
            this.showModal();
        })
    },

    methods: {
        showModal() {
            $('#add-survey-to-booking-modal').modal('show');
        },

        hideModal() {
            $('#add-survey-to-booking-modal').modal('hide');
            this.booking = null;
        },

        submit() {
            this.loading = true;

            http.post(`/app/survey-requests/add-to-booking`, {
                    booking_id: this.booking.id,
                    survey_id: this.survey_id,
                    sending_interval: this.sending_interval,
                    send_email: this.send_email,
                    send_sms: this.send_sms,
                })
                .then(({ data }) => {
                    this.loading = false;
                    toastr.success(data.success);
                    this.$event_bus.$emit('SurveyRequestAdded', data.survey_request);
                    this.hideModal();
                })
                .catch(({ error, errorBag }) => {
                    this.loading = false;
                    this.$setErrorBag(errorBag);
                });
        }
    },

    computed: {
        surveys() {
            return this.$store.getters['surveys/all'];
        },

        sms_switch_label() {
            let label = trans('surveys.send_by_sms');

            if (!this.is_survey_sendable_by_sms) {
                label += ' ('+trans('surveys.cannot_send_submit_sms')+')';
            } else if (this.booking && this.booking.client && this.is_survey_sendable_by_sms) {
                label += ' ('+trans('surveys.sent_to_phone', { phone : this.booking.client.phone })+')';
            }

            return label;
        },

        email_switch_label() {
            let label = trans('surveys.send_by_email');

            if (!this.is_survey_sendable_by_email ) {
                label += ' ('+trans('surveys.cannot_send_submit_email')+')';
            } else if (this.booking && this.booking.client && this.is_survey_sendable_by_email) {
                label += ' ('+trans('surveys.sent_to_email', { email : this.booking.client.email })+')';
            }

            return label;
        },

        is_survey_sendable_by_email() {
            return this.booking && this.booking.client && this.booking.client.email.trim() !== '';
        },

        is_survey_sendable_by_sms() {
            return this.canSendSmsMessages && this.booking && this.booking.client && this.booking.client.phone.trim() !== '';
        },

        survey_will_be_sent_now() {
            if (!this.booking) {
                return false;
            }

            let shouldBeSentAt = moment(this.booking.end);

            if (this.sending_interval.startsWith('-')) {
                let duration = moment.duration(this.sending_interval.replace('-', ''));
                shouldBeSentAt = moment(this.booking.start).subtract(duration);
            } else if (this.sending_interval.startsWith('+')) {
                let duration = moment.duration(this.sending_interval.replace('+', ''));
                shouldBeSentAt = moment(this.booking.end).add(duration);
            }

            return shouldBeSentAt.isBefore(moment());
        }
    },

    watch: {
        survey_id(newSurveyId, oldSurveyId) {
            if (newSurveyId !== oldSurveyId) {
                let followUpSurveys = this.$store.getters['surveys/followUpSurveysForSurvey'](newSurveyId);

                if (followUpSurveys && followUpSurveys.length > 0 && followUpSurveys[0].sending_interval !== this.sending_interval) {
                    this.sending_interval = followUpSurveys[0].sending_interval;
                }
            }
        },
    },

    components: {
        IntervalPicker,
        Switchery,
    },
}
</script>

<template>
    <Teleport to="body" :disabled="!portal">
        <div id="add-survey-to-booking-modal" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true" style="display: none;">
        <div class="modal-dialog">
                <div class="panel panel-color panel-inverse m-0">

                    <div class="panel-heading">
                        <button type="button" class="close" data-dismiss="modal" aria-hidden="true">×</button>
                        <h3 class="panel-title"><i class="zmdi zmdi-mail-send m-r-10"></i> {{ trans('surveys.add_survey') }}</h3>
                    </div>

                </div>

                <div class="modal-content">
                    <form @submit.prevent="submit">
                        <div class="modal-body p-0">
                            <div class="row" v-if="surveys.length">
                                <div class="col-xs-12">

                                    <ep-banner v-if="survey_will_be_sent_now">
                                        {{ trans('surveys.survey_will_be_sent_now') }}
                                    </ep-banner>

                                    <div class="form-group" :class="{ 'has-error': errors.has('survey_id') }">
                                        <label for="survey_id">{{ trans('surveys.survey') }}</label>
                                        <select id="survey_id" name="survey_id" class="form-control" v-model="survey_id" required>
                                            <option selected disabled :value="null">{{ trans('surveys.please_select') }}</option>
                                            <option v-for="survey in surveys" :key="survey.id" :value="survey.id" >{{ survey.name }}</option>
                                        </select>
                                        <span v-show="errors.has('survey_id')" class="help-block">{{ errors.first('survey_id') }}</span>
                                    </div>

                                    <div class="form-group" :class="{ 'has-error' : errors.has('sending_interval') }">
                                        <label class="control-label">{{ trans('follow_up.time_label') }}</label>

                                        <interval-picker v-model="sending_interval" :pre_label="trans('surveys.this_survey_should_be')" :post_label="trans('surveys.schedule_selection_suffix')"></interval-picker>

                                        <span v-show="errors.has('sending_interval')" class="help-block m-0">{{ errors.first('sending_interval') }}</span>
                                    </div>

                                    <div class="form-group">
                                        <switchery :text="email_switch_label" name="send_email" v-model="send_email" :editable="is_survey_sendable_by_email"></switchery>
                                        <switchery :text="sms_switch_label" name="send_sms" v-model="send_sms" :editable="is_survey_sendable_by_sms && canSendSmsMessages"></switchery>
                                        <small class="text-muted block m-b-10 dont-style-link" v-sanitize-html="trans('surveys.edit_content')"></small>
                                    </div>

                                    <small class="block m-b-10 text-muted" v-if="!canSendSmsMessages">
                                        {{ trans('settings.sms_please_pay') }}
                                        <a class="ml-2" href="" @click.prevent="$event_bus.$emit('showSubscriptionForm')">{{ trans('global.upgrade') }}</a>
                                    </small>

                                </div>
                            </div>

                            <ep-banner class="m-0" v-sanitize-html="trans('surveys.nothing_created')" v-else>
                            </ep-banner>
                        </div>
                        <div class="modal-footer" v-if="surveys.length">
                            <button type="submit" class="btn btn-custom m-t-20" :disabled="loading || (!send_email && !send_sms)">{{ trans('surveys.add_survey') }}</button>
                        </div>
                    </form>
                </div>
        </div>
        </div>
    </Teleport>
</template>
